import React, { useEffect, useRef, useState } from "react";
import {
  PiMagnifyingGlass,
  PiEye,
  PiUser,
  PiBuildings,
  PiUsers,
  PiScales,
  PiListChecks,
  PiKanban,
  PiPalette,
  PiRows,
  PiCreditCard,
  PiPlugsConnected,
} from "react-icons/pi";

const GROUPS = [
  {
    label: "You",
    scope: "Only affects your account",
    items: [
      { id: "profile", label: "Profile", icon: PiUser },
      { id: "integrations", label: "Integrations", icon: PiPlugsConnected },
    ],
  },
  {
    label: "Workspace",
    scope: "Applies to everyone in it",
    items: [
      { id: "general", label: "General", icon: PiBuildings, viewOnly: true },
      { id: "members", label: "Members", icon: PiUsers, viewOnly: true },
      { id: "scoring", label: "Go/No-Go scoring", icon: PiScales, viewOnly: true },
      { id: "screening", label: "Screening questions", icon: PiListChecks, viewOnly: true },
      { id: "statuses", label: "Project statuses", icon: PiKanban, viewOnly: true },
      { id: "design", label: "Proposal design", icon: PiPalette, viewOnly: true },
      { id: "sections", label: "Proposal sections", icon: PiRows, viewOnly: "reviewer" },
    ],
  },
  {
    label: "Plan",
    scope: "Managed by admins",
    items: [{ id: "billing", label: "Billing", icon: PiCreditCard, viewOnly: true }],
  },
];

const SettingsNav = ({ activeId, onSelect, canManage, roleKnown, orgRole, disabledIds = [] }) => {
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const q = query.trim().toLowerCase();
  const isViewOnly = (item) =>
    roleKnown && !canManage && (item.viewOnly === true || (item.viewOnly === "reviewer" && orgRole === "Reviewer"));

  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => !q || it.label.toLowerCase().includes(q)) }))
    .filter((g) => g.items.length > 0);

  return (
    <nav className="flex flex-col gap-5 font-outfit">
      <div className="px-2 flex flex-col gap-3.5">
        <span className="text-[20px] font-semibold text-[#18101C]">Settings</span>
        <label className="flex items-center gap-2 h-9 px-2.5 bg-white border border-black/[0.12] rounded-[8px] focus-within:border-[#8B55DD] focus-within:shadow-[0_0_0_3px_rgba(139,85,221,0.15)]">
          <PiMagnifyingGlass className="text-[16px] text-[#71717A] shrink-0" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setQuery("")}
            placeholder="Search settings"
            className="flex-1 min-w-0 bg-transparent outline-none text-[14px] text-[#18101C] placeholder:text-[#A1A1AA]"
          />
          <span className="text-[11px] text-[#71717A] border border-black/[0.12] rounded-[4px] px-[5px] py-px">⌘K</span>
        </label>
      </div>

      {groups.map((g) => (
        <div key={g.label} className="flex flex-col gap-0.5">
          <div className="px-2.5 pb-1.5 flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#71717A]">{g.label}</span>
            <span className="text-[12px] text-[#A1A1AA]">{g.scope}</span>
          </div>

          {g.items.map((it) => {
            const active = activeId === it.id;
            const disabled = disabledIds.includes(it.id);
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(it.id)}
                className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[8px] text-[14px] text-left transition-colors ${
                  active
                    ? "bg-[rgba(139,85,221,0.10)] text-[#8B55DD] font-semibold"
                    : "text-[#3F3F46] font-medium hover:bg-[#F4F0F7]"
                } ${disabled ? "opacity-50 cursor-not-allowed hover:bg-transparent" : ""}`}
              >
                <Icon className={`text-[18px] shrink-0 ${active ? "text-[#8B55DD]" : "text-[#71717A]"}`} />
                <span className="flex-1">{it.label}</span>
                {isViewOnly(it) && <PiEye className="text-[14px] text-[#A1A1AA] shrink-0" />}
              </button>
            );
          })}
        </div>
      ))}

      {groups.length === 0 && (
        <div className="px-2.5 text-[13px] text-[#A1A1AA]">No settings match "{query}"</div>
      )}

      {roleKnown && !canManage && !q && (
        <div className="mx-2.5 p-3 rounded-[8px] bg-[#F9FAFB] flex gap-2 text-[12px] leading-[1.45] text-[#71717A]">
          <PiEye className="text-[14px] mt-px shrink-0" />
          <span>View-only pages are marked with an eye.</span>
        </div>
      )}
    </nav>
  );
};

export default SettingsNav;
